import React, { useState } from 'react';
import { X, Save, Calendar, Clock } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EMPTY = {
  application_opens_at: '',
  application_closes_at: '',
};

const toDateInput = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d)) return '';
  return d.toISOString().slice(0, 10);
};

const IntakeModal = ({ intake, onSave, onClose, saving }) => {
  const [form, setForm] = useState(() =>
    intake
      ? {
          application_opens_at: toDateInput(intake.application_opens_at),
          application_closes_at: toDateInput(intake.application_closes_at),
        }
      : EMPTY
  );
  const [error, setError] = useState('');

  React.useEffect(() => {
    setForm(intake
      ? {
          application_opens_at: toDateInput(intake.application_opens_at),
          application_closes_at: toDateInput(intake.application_closes_at),
        }
      : EMPTY);
    setError('');
  }, [intake]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!intake) {
      return setError('This intake is generated automatically from the academic calendar.');
    }
    if (form.application_opens_at && form.application_closes_at && form.application_opens_at > form.application_closes_at) {
      return setError('Applications close date must be on or after the open date.');
    }
    try {
      await onSave(form);
    } catch (err) {
      setError(err?.message || 'Failed to save intake.');
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[calc(100vh-2rem)] w-full max-w-5xl overflow-y-auto rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold">Configure admission window</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Academic year and semester are generated from the Academic Calendar.
            </p>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2 text-slate-500">
                <Calendar className="h-4 w-4" />
                <span className="text-xs font-semibold uppercase tracking-wide">Academic period</span>
              </div>
              <p className="mt-2 text-lg font-semibold text-slate-900">{intake?.semester_ref?.name || intake?.name || 'Calendar period'}</p>
              <p className="mt-1 text-sm text-slate-600">{intake?.academic_year_ref?.name || intake?.academic_year || 'Academic year'} · {intake?.code || 'Automatic code'}</p>
              <p className="mt-3 text-xs text-slate-500">This identity is read from the configured academic year and semester and cannot be edited here.</p>
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-center gap-2 text-blue-700">
                <Clock className="h-4 w-4" />
                <span className="text-xs font-semibold uppercase tracking-wide">Application window</span>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-slate-700">Applications open</label>
                  <Input type="date" className="mt-1 bg-white" value={form.application_opens_at} onChange={(e) => set('application_opens_at', e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Applications close</label>
                  <Input type="date" className="mt-1 bg-white" value={form.application_closes_at} onChange={(e) => set('application_closes_at', e.target.value)} />
                </div>
              </div>
              <p className="mt-3 text-xs text-blue-800">Leave Applications open blank to open this intake immediately. It remains open until the close date; a blank close date means no closing date has been set.</p>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 lg:col-span-2">{error}</div>
          )}

          <div className="flex justify-end gap-3 border-t pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving…' : 'Save application window'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IntakeModal;
